import { Client } from "@/Common/Api/Client";
import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ImmunizationStatus } from "../Constants/ImmunizationStatus";
import { ValueSetType } from "../Constants/ValueSetType";
import { ImmunizationModel } from "../Models/ImmunizationModel";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { GetValuesRequest } from "../RequestModels/GetValuesRequest";
import { OverrideImmunizationStatusResponse } from "../ResponseModels/OverrideImmunizationStatusResponse";
import { ResetImmunizationStatusResponse } from "../ResponseModels/ResetImmunizationStatusResponse";
import { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps } from "./BaseImmunizationProfileSection";

interface IVaccinationOverrideStatusComponentProps extends IBaseImmunizationProfileSectionProps {
    immunization: ImmunizationModel;
}

class VaccinationOverrideStatusComponentState extends BaseImmunizationProfileSectionState {
    public reasonCode: string;
    public comment: string;
    public immunization: ImmunizationModel;

    constructor(init?: Partial<VaccinationOverrideStatusComponentState>) {
        super(init);
    }

    public static default(): VaccinationOverrideStatusComponentState {
        return new VaccinationOverrideStatusComponentState({
            ...BaseImmunizationProfileSectionState.default(),
            reasonCode: "",
            comment: "",
            immunization: null
        });
    }
}

class VaccinationOverrideStatusComponent extends BaseImmunizationProfileSection<VaccinationOverrideStatusComponentState, IVaccinationOverrideStatusComponentProps> {
    public constructor(props: IVaccinationOverrideStatusComponentProps) {
        super(props);

        const initialState = VaccinationOverrideStatusComponentState.default();
        initialState.immunization = props.immunization;

        this.state = initialState;
    }

    protected requestValues = new GetValuesRequest({
        Type: [ValueSetType.OverrideReason]
    });

    private getOverrideReasons(): KeyValuePair<string, string>[] {
        const reasonCodes = this.state.values.Types?.[ValueSetType.OverrideReason];

        if (!reasonCodes)
            return [];

        const overrideReasons = QuebecVaccinationRegistryHelper.getTranslatedDictionary(reasonCodes, ValueSetType.OverrideReason);

        return KeyValuePair.fromMapOfStrings(overrideReasons);
    }

    protected getBody(): JSX.Element {
        return (
            <div>
                {this.isForecastStatus && (
                    <FormFieldRow
                        labelValueKey="QuebecVaccinationRegistry_ReasonStatusChangeLabel"
                        isEditMode
                    >
                        <SelectKVPControl
                            Callback={(item: string) => this.mutateState(s => s.reasonCode = item)}
                            AvailableValues={this.getOverrideReasons()}
                            Value={this.state.reasonCode}
                            IncludeEmptyChoice
                            isInvalid={!this.state.reasonCode}
                        />
                    </FormFieldRow>
                )}
                <FormFieldRow
                    labelValueKey="CommentsLabel"
                    isEditMode
                >
                    <textarea
                        id="overrideCommentTextArea"
                        value={this.state.comment}
                        className={`form-control textarea-resize-vertical ${!this.state.comment ? "invalid" : ""}`}
                        rows={4}
                        onChange={(e: any) => this.onCommentChange(e.target.value)}
                    />
                </FormFieldRow>
            </div>
        );
    }

    protected canSave(): boolean {
        return !!this.state.comment
            && (!this.isForecastStatus || !!this.state.reasonCode);
    }

    protected get isForecastStatus(): boolean {
        const code = this.state.immunization.OverrideStatus.Code;

        return code === ImmunizationStatus.ForecastValid
            || code === ImmunizationStatus.ForecastInvalid;
    }

    protected async onSave(): Promise<void> {
        const response = await Client.postAsync<OverrideImmunizationStatusResponse | ResetImmunizationStatusResponse>(
            this.isForecastStatus
                ? ServerOperations.Server.QuebecVaccinationRegistry_OverrideImmunizationStatus
                : ServerOperations.Server.QuebecVaccinationRegistry_ResetImmunizationStatus,
            {
                ImmunizationId: this.state.immunization.Id,
                Version: this.state.immunization.Metadata.VersionId,
                Note: this.state.comment,
                ReasonCode: this.state.reasonCode,
                TransactionId: this.state.cachedTransactionId
            }
        );

        this.handleRvqResponse(response, () => this.props.updateImmunizationModel(response.data?.Immunization));
    }

    private onCommentChange(comment: string): void {
        this.mutateState(s => s.comment = comment);
    }
}

export { VaccinationOverrideStatusComponent };
